import { Fullscreen, History, MenuBook, Print } from '@mui/icons-material';
import {
  AppBar,
  Box,
  createTheme,
  Divider,
  IconButton,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  HomeAppBar,
  IconLabelButton,
  PageWithDrawer,
  useAppBarHeight,
  useDialog,
} from '@prismamedia/one-components';
import { OneDrawerMenu } from 'components/OneDrawerMenu';
import { PrintPortal } from 'components/PrintPortal';
import { FullscreenRailroad } from 'pages/IssuePage/Railroad/FullscreenRailroad';
import { RailroadFullPage } from 'pages/IssuePage/Railroad/utils';
import { useGetRailRoadPages } from 'pages/IssuePage/utils';
import { FC, useMemo, useState } from 'react';
import { theme } from 'theme';
import { QueryParamConfig, StringParam, useQueryParam } from 'use-query-params';
import { auth } from 'utils/auth';
import { DisplayOptions, Options } from './DisplayOptions';
import { FILTER_BAR_HEIGHT, FilterBar } from './FilterBar';
import { RailroadHistoryDialog } from './RailroadHistoryDialog';
import { RailroadPages } from './RailroadPages';
import { format } from 'date-fns';

export const RailroadPage: FC = () => {
  const appBarHeight = useAppBarHeight();
  const { openDialog } = useDialog();
  const [selectedIssue = undefined, setSelectedIssue] = useQueryParam<
    string,
    string | undefined
  >('issue', StringParam as QueryParamConfig<string, string | undefined>);

  const [displayOptions, setDisplayOptions] = useState<Options>({
    displayBorders: true,
    displayThumbnails: true,
    size: 4,
    miniature: false,
    displayInfos: true,
    displayPageNumber: true,
  });

  const {
    pages,
    publicationDate,
    comment,
    censhareCreationDate,
    censhareUpdateDate,
    loading,
    error,
  } = useGetRailRoadPages(selectedIssue);

  const fullPages = useMemo(() => {
    return (
      pages?.reduce<RailroadFullPage[]>((prev, page) => {
        const lastFullPage = prev[prev.length - 1];
        if (!lastFullPage || lastFullPage.length === 2 || prev.length === 1) {
          return [...prev, [page]];
        }

        // eslint-disable-next-line immutable/no-mutation
        prev[prev.length - 1] = [lastFullPage[0], page];
        return prev;
      }, []) || []
    );
  }, [pages]);

  return (
    <>
      <HomeAppBar
        appDrawerMenu={<OneDrawerMenu />}
        brandMenu={
          <Box overflow={'hidden'}>
            <Tooltip title={`Chemin de fer : ${comment}`}>
              <Typography
                variant="h6"
                sx={{
                  textWrap: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                Chemin de fer: {comment}
              </Typography>
            </Tooltip>
            {publicationDate && (
              <Typography
                variant="body2"
                sx={{
                  textWrap: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                Date de publication: {format(new Date(publicationDate), 'P')}
              </Typography>
            )}
          </Box>
        }
        currentUser={auth.user}
        disconnectUser={auth.logout}
        additionalElement={
          <ThemeProvider theme={createTheme({ palette: { mode: 'dark' } })}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {selectedIssue && (
                <>
                  <IconLabelButton
                    label="Imprimer"
                    onClick={() => window.print()}
                    sx={{ mr: 2 }}
                  >
                    <Print />
                  </IconLabelButton>
                  <IconLabelButton
                    label="Historique"
                    onClick={() => {
                      openDialog(
                        <RailroadHistoryDialog
                          censhareCreationDate={censhareCreationDate}
                          censhareUpdateDate={censhareUpdateDate}
                        />,
                      );
                    }}
                    edge="end"
                    size="large"
                  >
                    <History />
                  </IconLabelButton>
                </>
              )}
            </Box>
          </ThemeProvider>
        }
      />
      <AppBar
        position="fixed"
        color="default"
        sx={{
          top: appBarHeight,
          alignItems: 'center',
          flexDirection: 'row',
        }}
      >
        <FilterBar
          sx={{ mr: 'auto' }}
          selectedIssue={selectedIssue}
          setSelectedIssue={(v) => setSelectedIssue(v as string, 'replaceIn')}
        />
        {pages && (
          <>
            <Tooltip title="Afficher en plein écran">
              <IconButton
                sx={{ color: theme.palette.primary.main }}
                onClick={() => {
                  openDialog(<FullscreenRailroad pages={fullPages} />, {
                    fullScreen: true,
                  });
                }}
              >
                <Fullscreen />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" sx={{ height: 40, mx: 1 }} />
            <DisplayOptions
              sx={{ mr: 3 }}
              displayOptions={displayOptions}
              setDisplayOptions={setDisplayOptions}
            />
          </>
        )}
      </AppBar>
      <PageWithDrawer
        fullWidth
        sx={{ minHeight: '100%' }}
        paddingTop={FILTER_BAR_HEIGHT}
        error={error}
        loading={loading}
      >
        {selectedIssue ? (
          pages?.length ? (
            <RailroadPages
              pages={fullPages}
              displayOptions={displayOptions}
              comment={comment}
              publicationDate={publicationDate}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: theme.palette.primary.main,
              }}
            >
              <Box sx={{ fontSize: 25 }}>
                Aucun chemin de fer pour cette parution
              </Box>
            </Box>
          )
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: theme.palette.primary.main,
            }}
          >
            <MenuBook sx={{ fontSize: 40, mb: 2 }} />
            <Box sx={{ fontSize: 25 }}>
              Sélectionnez un magazine et une parution
            </Box>
          </Box>
        )}
      </PageWithDrawer>
      <PrintPortal>
        <RailroadPages
          pages={fullPages}
          displayOptions={displayOptions}
          comment={comment}
          publicationDate={publicationDate}
        />
      </PrintPortal>
    </>
  );
};
